import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateRange, DateRangePicker, PickersShortcutsItem } from '@mui/x-date-pickers-pro';

import { Experiences } from '@luxuryescapes/contract-svc-experience';

import { experiencesQueryKeys, useGetCollections } from '~/hooks/apis/experiences';

import {
  Collection,
  MetricsWindow,
  deleteCollectionOfferLink,
  getCarouselMetrics,
  getExperienceOffers,
  updateCollection,
} from '~/services/ExperiencesService';
import { Dayjs, getDayJs } from '~/services/TimeService';

import CollectionMetrics from './CollectionMetrics';

const OFFERS_PER_PAGE = 30;

const DATE_RANGE_SHORTCUTS: Array<PickersShortcutsItem<DateRange<Dayjs>>> = [
  {
    label: 'Last 7 Days',
    getValue: () => {
      const today = getDayJs();
      return [today.subtract(7, 'day'), today.subtract(1, 'day')];
    },
  },
  {
    label: 'Last 30 Days',
    getValue: () => {
      const today = getDayJs();
      return [today.subtract(30, 'day'), today.subtract(1, 'day')];
    },
  },
  {
    label: 'Last 90 Days',
    getValue: () => {
      const today = getDayJs();
      return [today.subtract(90, 'day'), today.subtract(1, 'day')];
    },
  },
];

type CustomMetricsState = {
  loading: boolean;
  data: Map<string, MetricsWindow> | null;
  error: string | null;
  startDate: string;
  endDate: string;
};

type CollectionOffersState = {
  loading: boolean;
  error: string | null;
  offers: Array<Experiences.Offer>;
  total: number;
  loaded: boolean;
};

export default function Collections() {
  const { data: collections = [], isLoading: loading, error: queryError } = useGetCollections(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [expandedCollection, setExpandedCollection] = useState<string | false>(false);
  const [collectionOffers, setCollectionOffers] = useState<Record<string, CollectionOffersState>>({});
  const [togglingCollections, setTogglingCollections] = useState<Set<string>>(new Set());
  const [removingOffers, setRemovingOffers] = useState<Set<string>>(new Set());
  const [collectionPages, setCollectionPages] = useState<Record<string, number>>({});
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([null, null]);
  const [customMetrics, setCustomMetrics] = useState<CustomMetricsState | null>(null);

  const brand = useSelector((state: App.State) => state.tenant.brand);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const error = queryError instanceof Error ? queryError.message : queryError ? 'Failed to fetch collections' : null;

  const filteredCollections = useMemo(() => {
    let filtered = collections;

    if (showOnlyActive) {
      filtered = filtered.filter((collection) => collection.isActive);
    }

    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (collection) =>
          collection.theme.toLowerCase().includes(lowerSearch) || collection.id.toLowerCase().includes(lowerSearch),
      );
    }

    return [...filtered].sort((a, b) => {
      const aOrders = a.metrics7d?.historical?.orders ?? 0;
      const bOrders = b.metrics7d?.historical?.orders ?? 0;
      return bOrders - aOrders;
    });
  }, [searchTerm, collections, showOnlyActive]);

  const fetchOffersForCollection = useCallback(
    async (collectionId: string, page = 0) => {
      setCollectionOffers((prev) => ({
        ...prev,
        [collectionId]: {
          ...(prev[collectionId] || { offers: [], total: 0 }),
          loading: true,
          error: null,
          loaded: false,
        },
      }));

      try {
        const response = await getExperienceOffers({
          brand,
          filters: { collectionIn: collectionId },
          limit: OFFERS_PER_PAGE,
          offset: page * OFFERS_PER_PAGE,
          curationData: true,
        });

        setCollectionOffers((prev) => ({
          ...prev,
          [collectionId]: {
            loading: false,
            error: null,
            offers: response.result || [],
            total: response.total || 0,
            loaded: true,
          },
        }));
      } catch (err) {
        setCollectionOffers((prev) => ({
          ...prev,
          [collectionId]: {
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to fetch offers',
            offers: [],
            total: 0,
            loaded: true,
          },
        }));
      }
    },
    [brand],
  );

  const handleToggleActive = useCallback(
    async (collection: Collection, newActiveState: boolean) => {
      setTogglingCollections((prev) => new Set(prev).add(collection.id));

      try {
        await updateCollection(collection.id, {
          isActive: newActiveState,
        });

        queryClient.setQueryData(experiencesQueryKeys.collectionsList(false), (prev: Array<Collection> | undefined) =>
          (prev ?? []).map((c) => (c.id === collection.id ? { ...c, isActive: newActiveState } : c)),
        );

        enqueueSnackbar(`Collection "${collection.theme}" ${newActiveState ? 'activated' : 'deactivated'}`, {
          variant: 'success',
        });
      } catch (err) {
        enqueueSnackbar(`Failed to update collection: ${err instanceof Error ? err.message : 'Unknown error'}`, {
          variant: 'error',
        });
      } finally {
        setTogglingCollections((prev) => {
          const next = new Set(prev);
          next.delete(collection.id);
          return next;
        });
      }
    },
    [enqueueSnackbar, queryClient],
  );

  const handleRemoveOfferFromCollection = useCallback(
    async (collectionId: string, offerId: string, offerTitle: string) => {
      const key = `${collectionId}:${offerId}`;
      setRemovingOffers((prev) => new Set(prev).add(key));

      try {
        await deleteCollectionOfferLink({ collectionId, offerId });

        // Remove the offer from local state
        setCollectionOffers((prev) => ({
          ...prev,
          [collectionId]: {
            ...prev[collectionId],
            offers: prev[collectionId].offers.filter((o) => o.id !== offerId),
          },
        }));

        enqueueSnackbar(`Removed "${offerTitle}" from collection`, { variant: 'success' });
      } catch (err) {
        enqueueSnackbar(`Failed to remove offer: ${err instanceof Error ? err.message : 'Unknown error'}`, {
          variant: 'error',
        });
      } finally {
        setRemovingOffers((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [enqueueSnackbar],
  );

  const handleAccordionChange = useCallback(
    (collectionId: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedCollection(isExpanded ? collectionId : false);

      if (isExpanded && !collectionOffers[collectionId]?.loaded) {
        setCollectionPages((prev) => ({ ...prev, [collectionId]: 0 }));
        fetchOffersForCollection(collectionId, 0);
      }
    },
    [collectionOffers, fetchOffersForCollection],
  );

  const handlePageChange = useCallback(
    (collectionId: string) => (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      setCollectionPages((prev) => ({ ...prev, [collectionId]: newPage }));
      fetchOffersForCollection(collectionId, newPage);
    },
    [fetchOffersForCollection],
  );

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleShowOnlyActiveChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlyActive(event.target.checked);
  }, []);

  const fetchRequestRef = useRef(0);

  const debouncedFetch = useMemo(
    () =>
      debounce(async (startDate: string, endDate: string) => {
        const requestId = ++fetchRequestRef.current;
        setCustomMetrics({ loading: true, data: null, error: null, startDate, endDate });

        try {
          const results = await getCarouselMetrics(startDate, endDate);
          if (fetchRequestRef.current !== requestId) return;
          const metricsMap = new Map<string, MetricsWindow>();
          for (const item of results) {
            metricsMap.set(item.carouselName, item.metrics);
          }
          setCustomMetrics({ loading: false, data: metricsMap, error: null, startDate, endDate });
        } catch (err) {
          if (fetchRequestRef.current !== requestId) return;
          setCustomMetrics({
            loading: false,
            data: null,
            error: err instanceof Error ? err.message : 'Failed to fetch custom range metrics',
            startDate,
            endDate,
          });
        }
      }, 500),
    [],
  );

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  const handleDateRangeChange = useCallback(
    (newRange: DateRange<Dayjs>) => {
      setDateRange(newRange);
      const [start, end] = newRange;

      if (!start || !end || !start.isValid() || !end.isValid()) {
        debouncedFetch.cancel();
        setCustomMetrics(null);
        return;
      }

      debouncedFetch(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
    },
    [debouncedFetch],
  );

  const findMetricsForTheme = useCallback(
    (theme: string): MetricsWindow | null => {
      if (!customMetrics?.data) return null;
      const exact = customMetrics.data.get(theme);
      if (exact) return exact;
      for (const [key, metrics] of customMetrics.data.entries()) {
        const lastSegment = key.split(' - ').pop();
        if (lastSegment === theme) return metrics;
      }
      return null;
    },
    [customMetrics],
  );

  const getCurationStatusColor = (status?: string): 'success' | 'error' | 'warning' => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      default:
        return 'warning';
    }
  };

  const getCurationStatusLabel = (status?: string): string => {
    switch (status) {
      case 'APPROVED':
        return 'Approved';
      case 'REJECTED':
        return 'Rejected';
      default:
        return 'Not Curated';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const activeCount = collections.filter((c) => c.isActive).length;
  const inactiveCount = collections.length - activeCount;

  return (
    <Stack direction="column" spacing={3}>
      <Typography variant="h5" fontWeight="bold">
        Experience Collections
      </Typography>

      <Stack direction="row" spacing={3} alignItems="center">
        <Box maxWidth={400} flexGrow={1}>
          <TextField
            fullWidth
            label="Search collections"
            placeholder="Search by theme or ID..."
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
          />
        </Box>
        <FormControlLabel
          control={<Switch checked={showOnlyActive} onChange={handleShowOnlyActiveChange} />}
          label="Show only active"
        />
        <DateRangePicker
          value={dateRange}
          onChange={handleDateRangeChange}
          maxDate={getDayJs().subtract(1, 'day')}
          localeText={{ start: 'Start date', end: 'End date' }}
          slotProps={{
            shortcuts: { items: DATE_RANGE_SHORTCUTS },
            textField: { size: 'small' },
          }}
        />
        {customMetrics?.loading && <CircularProgress size={20} />}
      </Stack>

      {customMetrics?.error && <Alert severity="error">{customMetrics.error}</Alert>}

      <Stack direction="row" spacing={2}>
        <Alert severity="info" sx={{ flexGrow: 1 }}>
          Found {filteredCollections.length} collection{filteredCollections.length !== 1 ? 's' : ''}
          {searchTerm && ` matching "${searchTerm}"`}
          {showOnlyActive && ' (active only)'}
        </Alert>
        <Chip label={`${activeCount} active`} color="success" variant="outlined" />
        <Chip label={`${inactiveCount} inactive`} color="default" variant="outlined" />
      </Stack>

      {filteredCollections.length === 0 ? (
        <Alert severity="warning">No collections found</Alert>
      ) : (
        <Box>
          {filteredCollections.map((collection) => {
            const offersState = collectionOffers[collection.id];
            const isToggling = togglingCollections.has(collection.id);
            const themeMetrics = customMetrics?.data ? findMetricsForTheme(collection.theme) : null;
            const customRangeData =
              themeMetrics && customMetrics?.startDate && customMetrics?.endDate
                ? { metrics: themeMetrics, startDate: customMetrics.startDate, endDate: customMetrics.endDate }
                : null;

            return (
              <Accordion
                key={collection.id}
                expanded={expandedCollection === collection.id}
                onChange={handleAccordionChange(collection.id)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={2} alignItems="center" width="100%">
                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                      {isToggling ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Switch
                          checked={collection.isActive}
                          size="small"
                          color="success"
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => handleToggleActive(collection, e.target.checked)}
                        />
                      )}
                    </Box>
                    <Chip
                      label={collection.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={collection.isActive ? 'success' : 'default'}
                      variant={collection.isActive ? 'filled' : 'outlined'}
                    />
                    <Typography fontWeight="bold" flexGrow={1}>
                      {collection.theme}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                      {collection.id}
                    </Typography>
                    {collection.metrics7d && (
                      <Chip
                        label={`${collection.metrics7d.last7d.orders} orders (7d)`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    )}
                    {offersState?.loaded && (
                      <Chip
                        label={`${offersState.total} offer${offersState.total !== 1 ? 's' : ''}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <CollectionMetrics
                    metrics={collection.metrics7d}
                    isActive={collection.isActive}
                    activatedAt={collection.activatedAt}
                    customRange={customRangeData}
                  />

                  {collection.definition && (
                    <Box mb={2} p={2} bgcolor="grey.50" borderRadius={1}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Definition:</strong> {collection.definition}
                      </Typography>
                    </Box>
                  )}

                  {offersState?.loading && (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  )}

                  {offersState?.error && <Alert severity="error">{offersState.error}</Alert>}

                  {offersState?.loaded && offersState.total === 0 && (
                    <Alert severity="info">No offers in this collection</Alert>
                  )}

                  {offersState?.loaded && offersState.total > 0 && (
                    <>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <Typography fontWeight="bold" variant="body2">
                                Title
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight="bold" variant="body2">
                                ID
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight="bold" variant="body2">
                                Provider
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight="bold" variant="body2">
                                Status
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight="bold" variant="body2">
                                Curation
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography fontWeight="bold" variant="body2">
                                Actions
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {offersState.offers.map((offer) => (
                            <TableRow key={offer.id} hover>
                              <TableCell>
                                <Link href={`/experiences/${offer.id}`} target="_blank" underline="hover">
                                  {offer.title}
                                </Link>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                                  {offer.id}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip label={offer.provider} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={offer.status === 'ONLINE' ? 'Online' : 'Unavailable'}
                                  size="small"
                                  color={offer.status === 'ONLINE' ? 'success' : 'error'}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getCurationStatusLabel(offer.curationStatus)}
                                  size="small"
                                  color={getCurationStatusColor(offer.curationStatus)}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="center">
                                {removingOffers.has(`${collection.id}:${offer.id}`) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <Tooltip title="Remove from collection">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        handleRemoveOfferFromCollection(collection.id, offer.id, offer.title)
                                      }
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <TablePagination
                        component="div"
                        count={offersState.total}
                        page={collectionPages[collection.id] || 0}
                        onPageChange={handlePageChange(collection.id)}
                        rowsPerPage={OFFERS_PER_PAGE}
                        rowsPerPageOptions={[OFFERS_PER_PAGE]}
                        labelDisplayedRows={({ from, to, count }) =>
                          `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                        }
                      />
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Stack>
  );
}
